import {
  UpdateWordLibraryBlock,
  type WordLibraryPreviousValues,
} from '@/components/chat/components/UpdateWordLibraryBlock';
import type { ToolUseItem } from '@heidi/agents/contracts';
import { isRecord } from '@heidi/agents/contracts';

import { friendlyToolError } from '@heidi/agents/data';
import { readNumber, readToolData, readToolError } from './shared/readers';
import { toCreateTemplateStatus } from './status';

/** Read find→replace pairs from the tool input `entries` object. */
const readEntries = (
  input: Record<string, unknown>
): Array<{ from: string; to: string }> => {
  const raw = input.entries;
  if (!isRecord(raw)) return [];
  return Object.entries(raw)
    .filter(
      (pair): pair is [string, string] =>
        typeof pair[0] === 'string' && typeof pair[1] === 'string'
    )
    .map(([from, to]) => ({ from, to }));
};

/** Optional prior values from tool result `previous` / `previous_values`. */
const readPreviousValues = (
  data: Record<string, unknown> | undefined
): WordLibraryPreviousValues | undefined => {
  if (!data) return undefined;
  const raw = data.previous ?? data.previous_values;
  if (!isRecord(raw)) return undefined;
  const out: WordLibraryPreviousValues = {};
  let any = false;
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === 'string' || value === null) {
      out[key] = value;
      any = true;
    }
  }
  return any ? out : undefined;
};

export const renderUpdateWordLibraryToolUse = (item: ToolUseItem): JSX.Element | null => {
  const status = toCreateTemplateStatus(item.status);
  if (!status) return null;

  const entries = readEntries(item.input);
  const data = readToolData(item);

  if (status === 'error') {
    return (
      <UpdateWordLibraryBlock
        status="error"
        entries={entries}
        error={friendlyToolError(readToolError(item))}
        toolId={item.id}
      />
    );
  }

  return (
    <UpdateWordLibraryBlock
      status={status}
      entries={entries}
      added={readNumber(data, 'added')}
      updated={readNumber(data, 'updated')}
      previousValues={readPreviousValues(data)}
      toolId={item.id}
    />
  );
};
